import { ZodObject, ZodRawShape, ZodSchema } from 'zod';
import { Lifecycle, Request, ResponseObject, ResponseToolkit } from '@hapi/hapi';

interface ValidatorSchemas {
    payload?: ZodSchema;
    headers?: ZodSchema;
    query?: ZodSchema;
}

export const validateWithSchema =
    (schema?: ZodSchema) =>
    (value: any): any => {
        if (schema) {
            const result = schema.safeParse(value);
            if (result.success) {
                return value;
            }
            const errors = result.error.errors.map((err) => ({
                path: err.path.join('.'),
                message: err.message,
            }));
            throw new Error(JSON.stringify(errors));
        }
        return value;
    };

export const createValidator = ({
    payload,
    headers,
    query,
}: ValidatorSchemas): {
    payload?: Lifecycle.Method;
    headers?: Lifecycle.Method;
    query?: Lifecycle.Method;
    failAction: Lifecycle.FailAction;
} => ({
    payload: payload ? validateWithSchema(payload) : undefined,
    headers: headers ? validateWithSchema(headers) : undefined,
    query: query ? validateWithSchema(query) : undefined,
    failAction: (request: Request, h: ResponseToolkit, err?: Error): ResponseObject => {
        if (err) {
            const error = JSON.parse(err.message);
            const errors = error.map((e: any) => ({
                path: e.path,
                message: e.message,
            }));
            return h.response({ errors }).code(400).takeover();
        }
        return h
            .response({ errors: [{ message: 'Unknown validation error' }] })
            .code(400)
            .takeover();
    },
});

/**
 * Extracts field names from a Zod schema as an array of strings.
 * @param schema - The Zod schema from which to extract the field names.
 * @returns An array of field names as strings.
 */
export const getFieldNames = (schema: ZodObject<ZodRawShape>): string[] => {
    // Get the keys from the schema's shape
    const fieldNames = Object.keys(schema.shape);

    // Convert the keys to an array of strings (though they are already strings)
    return fieldNames.map(String);
};
